import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DATA_SETS_MAP_DEFAULT_VALUES, DATA_SETS_MAP_PRODUCT_SUPPLIERS, DATA_SETS_MAP_SUPPLIERS } from 'constants/dataSets';
import { DATA_SET_UPLOAD_FILE } from 'constants/formFields';
import { PRESIGNED_FILE_TYPES } from 'constants/interfaces';
import { useAppDispatch } from 'store/hooks/useApp';
import { setDataSetsState } from 'store/slices/dataSets/slice';

import useDataSetsUploadForm from './useDataSetsUploadForm';
import useFileStorageUpload from './useFileStorageUpload';

import type { TPresignedFileTypes } from 'constants/interfaces';
import type {
  TUseProfitCenterImportRecordsMutation,
} from 'pages/UserPages/ProfitCentres/interfaces/TUseProfitCenterImportRecordsMutation';
import type IUseDownloadExample from './interfaces/IUseDownloadExample';

function useDownloadExample<T extends TUseProfitCenterImportRecordsMutation>({
  importRecords,
  onError,
}: IUseDownloadExample<T>) {
  const [isMappingModalVisible, setIsMappingModalVisible] = useState(false);
  const [isImportErrorsModalVisible, setIsImportErrorsModalVisible] = useState(false);
  const { control: uploadControl, watch, reset } = useDataSetsUploadForm();
  const dataSetFile = watch(DATA_SET_UPLOAD_FILE);
  const dispatch = useAppDispatch();
  const { uploadToStorage, isLoading } = useFileStorageUpload();
  const { pathname } = useLocation();

  const uploadFile = useCallback((file: File | string, fileType: TPresignedFileTypes) => {
    if (typeof file === 'object') {
      return uploadToStorage(file, fileType)
        .then((presignedData) => presignedData.fields.Url)
        .catch(() => '');
    }
    return Promise.resolve(file);
  }, [uploadToStorage]);

  const getCurrentOption = useCallback(() => {
    let currentOption = DATA_SETS_MAP_DEFAULT_VALUES;
    const currentPageProducts = pathname.includes('products');
    const currentPageSuppliers = pathname.includes('suppliers');
    const currentPageCustomers = pathname.includes('customers');
    if (currentPageProducts) {
      currentOption = DATA_SETS_MAP_PRODUCT_SUPPLIERS;
    } else if (currentPageSuppliers || currentPageCustomers) {
      currentOption = DATA_SETS_MAP_SUPPLIERS;
    }
    return currentOption;
  }, [pathname]);

  useEffect(() => {
    if (dataSetFile && !isLoading) {
      const currentOption = getCurrentOption();
      uploadFile(dataSetFile, PRESIGNED_FILE_TYPES.dataSet).then((url) => importRecords({
        url,
        ...currentOption,
      }).unwrap().then((response) => {
        dispatch(setDataSetsState({ mappingColumns: response.data }));
        setIsMappingModalVisible(true);
      })).catch((error: unknown) => {
        if (onError) {
          onError(error);
        }
      });
      reset({
        [DATA_SET_UPLOAD_FILE]: '',
      });
    }
  }, [dataSetFile, uploadFile, reset, isLoading, importRecords,
    dispatch, setIsMappingModalVisible, pathname, getCurrentOption, onError]);

  const onCloseMappingModal = () => {
    setIsMappingModalVisible(false);
  };

  return {
    uploadControl,
    isMappingModalVisible,
    onCloseMappingModal,
    isImportErrorsModalVisible,
    setIsImportErrorsModalVisible,
  };
}

export default useDownloadExample;
